'use client'

import * as React from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Calendar, DollarSign, Users } from 'lucide-react'
import { doc, getDoc } from 'firebase/firestore'
import { Badge } from 'components/ui/badge'
import { Button } from 'components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from 'components/ui/card'
import { Progress } from 'components/ui/progress'
import { Separator } from 'components/ui/separator'
import { toast } from 'sonner'
import { db } from '../../../../firebase'
import { useUser } from 'components/context/user-context'
import { getUserId, isUserAssociatedWithAward } from 'lib/access'

const statusColors = {
  active: 'bg-success/15 text-success border-success/30',
  closed: 'bg-muted text-muted-foreground border-border',
  pending: 'bg-warning/15 text-warning border-warning/30',
}

const normalizeDate = (value) => {
  if (!value) return null
  if (value?.toDate) return value.toDate()
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

export default function AwardDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user, accessLevel } = useUser()
  const [award, setAward] = React.useState(null)
  const [isLoading, setIsLoading] = React.useState(true)

  React.useEffect(() => {
    let isMounted = true

    async function loadAward() {
      if (!id) return
      setIsLoading(true)
      try {
        const snapshot = await getDoc(doc(db, 'awards', id))
        if (!snapshot.exists()) {
          toast.error('Award not found')
          navigate('/dashboard/awards')
          return
        }
        if (!isMounted) return
        const record = { id: snapshot.id, ...snapshot.data() }
        const limited = accessLevel === 'limited'
        const currentUserId = getUserId(user)
        if (limited && !isUserAssociatedWithAward(record, currentUserId)) {
          toast.error('You do not have access to this award.')
          navigate('/dashboard/awards')
          return
        }
        setAward(record)
      } catch (error) {
        toast.error('Failed to load award', {
          description: error?.message || 'Please try again.',
        })
      } finally {
        if (isMounted) {
          setIsLoading(false)
        }
      }
    }

    loadAward()

    return () => {
      isMounted = false
    }
  }, [id, navigate, accessLevel, user?.userId, user?.uid])

  if (isLoading) {
    return (
      <div className="p-6">
        <p className="text-sm text-muted-foreground">Loading award...</p>
      </div>
    )
  }

  if (!award) return null

  const startDate = normalizeDate(award.startDate)
  const endDate = normalizeDate(award.endDate)
  const now = new Date()
  const totalDuration = startDate && endDate ? endDate.getTime() - startDate.getTime() : 0
  const elapsed = startDate ? now.getTime() - startDate.getTime() : 0
  const timeProgress =
    totalDuration > 0 ? Math.min(100, Math.max(0, (elapsed / totalDuration) * 100)) : 0

  return (
    <div className="flex flex-col gap-6 p-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" asChild className="size-8">
          <Link to="/dashboard/awards">
            <ArrowLeft className="size-4" />
          </Link>
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-foreground font-serif tracking-tight">
            {award.title || 'Award'}
          </h1>
          <p className="text-sm text-muted-foreground">
            {award.grantNumber || 'Grant number not set'}
          </p>
        </div>
        <Badge className={`ml-auto text-[10px] border ${statusColors[award.status] || statusColors.pending}`}>
          {award.status || 'pending'}
        </Badge>
      </div>

      <div className="grid gap-4 lg:grid-cols-[2fr_1fr]">
        <Card className="border border-border">
          <CardHeader>
            <CardTitle className="text-base">Award Overview</CardTitle>
            <CardDescription>Key details for this grant or award.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 text-sm">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <p className="text-xs text-muted-foreground">Donor</p>
                <p className="text-foreground">{award.donorName || '—'}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Linked Proposal</p>
                {award.proposalId ? (
                  <Link
                    to={`/dashboard/proposals/${award.proposalId}`}
                    className="text-primary hover:underline"
                  >
                    {award.proposalTitle || award.proposalId}
                  </Link>
                ) : (
                  <p className="text-foreground">—</p>
                )}
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Total Budget</p>
                <p className="text-foreground">
                  {award.currency || 'USD'}{' '}
                  {award.totalBudget ? Number(award.totalBudget).toLocaleString('en-KE') : '0'}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Start Date</p>
                <p className="text-foreground">
                  {startDate ? startDate.toLocaleDateString('en-KE') : '—'}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">End Date</p>
                <p className="text-foreground">
                  {endDate ? endDate.toLocaleDateString('en-KE') : '—'}
                </p>
              </div>
            </div>

            <Separator />

            <div>
              <div className="flex items-center justify-between text-[10px] text-muted-foreground">
                <span>Timeline progress</span>
                <span>{Math.round(timeProgress)}%</span>
              </div>
              <Progress value={timeProgress} className="h-1.5 mt-1.5" />
            </div>

            <Separator />

            <div className="grid gap-3 sm:grid-cols-3">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Users className="size-3.5" />
                <span>{(award.members || []).length} members</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Calendar className="size-3.5" />
                <span>
                  {endDate ? endDate.toLocaleDateString('en-KE', { month: 'short', year: 'numeric' }) : '—'}
                </span>
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <DollarSign className="size-3.5" />
                <span>{award.currency || 'USD'}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-border">
          <CardHeader>
            <CardTitle className="text-base">Award Members</CardTitle>
            <CardDescription>People associated with this award.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            {(award.members || []).length === 0 ? (
              <p className="text-sm text-muted-foreground">No members added.</p>
            ) : (
              <ul className="space-y-1">
                {award.members.map((member, index) => (
                  <li key={`${member}-${index}`} className="text-foreground">
                    {member}
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
